import {
  FC,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Control, useController } from 'react-hook-form';
import styled, { css } from 'styled-components';

import ValidationMessage from '@/components/Forms/ValidationMessage';
import useAlerts from '@/hooks/useAlerts';
import useOnClickOutside from '@/hooks/useOnClickOutside';

import { FormDataT } from './ScriptPart';

const Textarea = styled.textarea`
  width: 100%;
  border: 0;
  outline: none;
  font-size: 14px;
  line-height: 22px;
  color: ${({ theme }) => theme.colors.black};
  font-family: 'Manrope';
  overflow-y: hidden;
  resize: none;
  flex: 1;
  padding: 0;
  font-weight: 600;
  &::selection {
    background: #b7daf8;
  }
`;

const StyledValidationMessage = styled(ValidationMessage)`
  display: inline-flex;
`;

const Wrap = styled.div<{ $withoutPaddingLeft: boolean }>`
  flex: 1;
  padding: 0 24px;
  position: relative;

  ${({ $withoutPaddingLeft }) =>
    $withoutPaddingLeft &&
    css`
      padding-left: 0;
    `}
`;

const SelectionButton = styled.button<{ $top: number; $left: number }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.purpleLighter};
  position: absolute;
  top: ${({ $top }) => $top - 45}px;
  left: ${({ $left }) => $left}px;
`;

interface Props {
  control: Control<FormDataT>;
  name: 'text.a' | 'text.b' | 'splittedPart.text';
  appendSubPart: (text: string) => void;
  removeSubPart?: VoidFunction;
  activeVariation?: string;
}

const ScriptField: FC<Props> = ({
  control,
  name,
  appendSubPart,
  removeSubPart,
  activeVariation,
}) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const alerts = useAlerts();
  const [selectionState, setSelectionState] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);

  const {
    field: { value, onChange, ref },
    fieldState: { error },
  } = useController<FormDataT, 'text.a' | 'text.b' | 'splittedPart.text'>({
    name,
    control,
  });

  const onMouseUp = (e: MouseEvent<HTMLTextAreaElement>) => {
    if (activeVariation !== name.split('.')[1]) {
      return;
    }

    const selection = window.getSelection();

    const text = selection?.toString();

    if (!text) {
      return setSelectionState(null);
    }

    const rect = inputRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left; //x position within the element.
    const y = e.clientY - rect.top; //y position within the element.

    setSelectionState({ text, x, y });
  };

  const onCutSelection = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();
      const confirmed = await alerts.confirm(
        'Cut selected text to another part?',
        selectionState!.text,
      );

      if (!confirmed) {
        return setSelectionState(null);
      }

      onChange(value.replace(selectionState!.text, ''));
      appendSubPart(selectionState!.text);
      setSelectionState(null);

      alerts.close();
    } catch (_) {
      alerts.error();
      setSelectionState(null);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (value === '' && e.key === 'Backspace' && name === 'splittedPart.text') {
      removeSubPart?.();
    }
  };

  useOnClickOutside(inputRef, () => setSelectionState(null));

  useEffect(() => {
    const { current: el } = inputRef;

    if (!el) {
      return;
    }

    el.style.height = '0';
    el.style.height = el.scrollHeight + 'px';
  }, [value]);

  return (
    <Wrap $withoutPaddingLeft={name === 'splittedPart.text'}>
      <Textarea
        ref={(el) => {
          ref(el);
          inputRef.current = el;
        }}
        onMouseUp={onMouseUp}
        placeholder="Add translation here"
        value={value}
        onKeyDown={onKeyDown}
        onChange={onChange}
      />
      {!!error && (
        <StyledValidationMessage>{error.message}</StyledValidationMessage>
      )}
      {!!selectionState && (
        <SelectionButton
          $top={selectionState.y}
          $left={selectionState.x}
          onClick={onCutSelection}
          type="button"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 7.5L2 10.5L5 13.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M5 3.5H10.5C11.4283 3.5 12.3185 3.86875 12.9749 4.52513C13.6313 5.1815 14 6.07174 14 7V7.00001C14 7.45963 13.9095 7.91476 13.7336 8.3394C13.5577 8.76404 13.2999 9.14987 12.9749 9.47488C12.6499 9.79988 12.264 10.0577 11.8394 10.2336C11.4148 10.4095 10.9596 10.5 10.5 10.5H2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </SelectionButton>
      )}
    </Wrap>
  );
};

export default ScriptField;
